/**
 * Utility functions để xử lý fallback khi không có hỗ trợ notifications
 */

export interface NotificationFallbackOptions {
  title: string;
  message: string;
  duration?: number;
  type?: 'info' | 'warning' | 'error' | 'success';
}

export class NotificationFallback {
  private static toastContainer: HTMLElement | null = null;

  // Kiểm tra có hỗ trợ notifications không
  static isNotificationSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator
    );
  }

  // Hiển thị toast notification thay thế
  static showToast(options: NotificationFallbackOptions): void {
    const { title, message, duration = 5000, type = 'info' } = options;

    // Tạo container nếu chưa có
    if (!this.toastContainer) {
      this.createToastContainer();
    }

    // Tạo toast element
    const toast = document.createElement('div');
    toast.className = `notification-toast toast-${type}`;
    
    // Styling cho toast
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      minWidth: '300px',
      maxWidth: '400px',
      padding: '16px',
      borderRadius: '8px',
      color: 'white',
      fontSize: '14px',
      zIndex: '10000',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease-in-out',
      marginBottom: '10px',
      backgroundColor: this.getToastColor(type),
      cursor: 'pointer'
    });

    // Nội dung toast
    toast.innerHTML = `
      <div style="display: flex; align-items: flex-start; justify-content: space-between;">
        <div style="flex: 1;">
          <div style="font-weight: bold; margin-bottom: 4px;">
            ${this.getToastIcon(type)} ${title}
          </div>
          <div style="line-height: 1.4; opacity: 0.9;">
            ${message}
          </div>
        </div>
        <span style="margin-left: 12px; font-size: 18px; cursor: pointer; opacity: 0.7; hover: { opacity: 1 }">
          ×
        </span>
      </div>
    `;

    // Thêm vào container
    this.toastContainer!.appendChild(toast);

    // Animation slide in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
    }, 10);

    // Xử lý click để đóng
    const closeButton = toast.querySelector('span');
    const closeToast = () => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    };

    closeButton?.addEventListener('click', closeToast);
    toast.addEventListener('click', closeToast);

    // Tự động đóng sau duration
    if (duration > 0) {
      setTimeout(closeToast, duration);
    }
  }

  // Tạo container cho toasts
  private static createToastContainer(): void {
    this.toastContainer = document.createElement('div');
    this.toastContainer.id = 'notification-toast-container';
    Object.assign(this.toastContainer.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      zIndex: '10000',
      pointerEvents: 'none'
    });
    
    // Cho phép click trên toast elements
    this.toastContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Thiết lập pointer events cho children
    const style = document.createElement('style');
    style.textContent = `
      #notification-toast-container .notification-toast {
        pointer-events: auto;
      }
      #notification-toast-container .notification-toast:hover {
        transform: translateX(-5px) !important;
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(this.toastContainer);
  }

  // Lấy màu theo type
  private static getToastColor(type: string): string {
    switch (type) {
      case 'success': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'error': return '#f44336';
      default: return '#2196F3';
    }
  }

  // Lấy icon theo type
  private static getToastIcon(type: string): string {
    switch (type) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '🚫';
      default: return 'ℹ️';
    }
  }

  // Hiển thị notification với fallback tự động
  static notify(options: NotificationFallbackOptions): void {
    const { title, message } = options;

    // Thử sử dụng browser notification trước
    if (this.isNotificationSupported() && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico'
        });
        return;
      } catch (error) {
        console.warn('Failed to show browser notification, falling back to toast:', error);
      }
    }

    // Fallback sang toast
    this.showToast(options);
  }

  // Xóa tất cả toasts
  static clearAll(): void {
    if (this.toastContainer) {
      this.toastContainer.innerHTML = '';
    }
  }

  // Hiển thị cảnh báo về việc không hỗ trợ notifications
  static showUnsupportedWarning(): void {
  
  }
}

// Export default instance
export default NotificationFallback;
